/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Icon, IIconStyleProps, IIconStyles } from '@fluentui/react/lib/Icon';
import { ILinkStyleProps, ILinkStyles, Link } from '@fluentui/react/lib/Link';
import { IStackProps, IStackStyles, Stack } from '@fluentui/react/lib/Stack';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme, getStylistV2, important } from 'src/core/themes/luisStylist';
import { LuisKeyCodes } from 'src/core/utils/keyCodes';

export type DragAndDropInputProps = {
    disabled?: boolean;
    isMultiple?: boolean;
    showUploadedFiles?: boolean;
    extensionsToAccept?: string;
    onDropFiles: (files: File[]) => void;
    onRenderContent?: (defaultRenderer: React.ReactNode) => React.ReactNode;
};

const messages = defineMessages({
    or: { id: 'core.dragAndDropInput.or', defaultMessage: 'or' },
    browseFile: { id: 'core.dragAndDropInput.browseFile', defaultMessage: 'Browse for a file' },
    dragFileLabel: { id: 'core.dragAndDropInput.dragFileLabel', defaultMessage: 'Drag and drop {extension} file(s) here' }
});

const theme = getScopedTheme('DragAndDropInput');
const { getClassName } = getStylistV2('DragAndDropInput');

const inputFilePickerClassName = getClassName('inputFilePickerClassName', important({ display: 'none' }));
const linkStyles = (isDisabled: boolean) =>
    classNamesFunction<ILinkStyleProps, ILinkStyles>()({
        root: { textAlign: 'center', fontSize: 14, color: !isDisabled && theme.linkColor }
    });
const iconStyles = classNamesFunction<IIconStyleProps, IIconStyles>()({ root: { fontSize: '35px', color: theme.iconColor } });
const containerStyles = classNamesFunction<IStackProps, IStackStyles>()({
    root: { backgroundColor: theme.backgroundColor, outline: `1px dashed ${theme.borderColor}` }
});

export const DragAndDropInput = (props: DragAndDropInputProps) => {
    const { disabled, extensionsToAccept = '.txt', onDropFiles, onRenderContent, showUploadedFiles, isMultiple = true } = props;

    const { localize } = useLocalization();
    const [uploadedFiles, setUploadedFiles] = React.useState<File[]>();
    const [isDragging, setIsDragging] = React.useState<boolean>(false);

    const inputRef = React.useRef<HTMLInputElement>(null);
    const shouldListUploadedFiles = showUploadedFiles && uploadedFiles?.length;
    const draggingStyles = isDragging ? { root: { outline: `2px dashed ${theme.borderColorHovered}` } } : {};

    const updateFiles = (files: File[]) => {
        onDropFiles(files);
        setUploadedFiles(files);
    };

    const handleDrop = (event: React.DragEvent<HTMLElement>) => {
        event.preventDefault();
        event.stopPropagation();
        updateFiles(Array.from(event.dataTransfer.files));
        setIsDragging(false);
    };

    const onDragOver = (event: React.DragEvent<HTMLElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragging(true);
    };

    const onSubmitBrowsedFile = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const { files } = event.target as HTMLInputElement;

        if (files) {
            updateFiles([...Array.from(files)]);
        }

        // We need to clear the value of the event target to allow the user to re-upload
        // the same file and reset the content shown in the text box.
        event.target.value = '';
    };

    const renderFiles = (files: File[]) =>
        files.map((file, index) => (
            <LuisText key={`${file.name}-${index}`} block>
                {file.name}
            </LuisText>
        ));

    const defaultRenderer = (
        <Stack data-automation-id="defaultRenderer" horizontalAlign="center">
            {shouldListUploadedFiles ? (
                renderFiles(uploadedFiles)
            ) : (
                <>
                    <Icon iconName="CloudUpload" styles={iconStyles} />
                    <LuisText styles={{ root: { textAlign: 'center' } }}>
                        {localize(messages.dragFileLabel, { extension: extensionsToAccept })}
                    </LuisText>
                    <LuisText>{localize(messages.or)}</LuisText>
                </>
            )}

            <Link
                disabled={disabled}
                as="a"
                tabIndex={0}
                onClick={() => inputRef.current.click()}
                styles={linkStyles(disabled)}
                onKeyUp={e => {
                    if (e.key === LuisKeyCodes.Enter) {
                        inputRef.current.click();
                    }
                }}>
                {shouldListUploadedFiles
                    ? `${localize(messages.dragFileLabel, { extension: extensionsToAccept })} ${localize(messages.or)} ${localize(
                          messages.browseFile
                      )}`
                    : localize(messages.browseFile)}
            </Link>

            <input
                type="file"
                ref={inputRef}
                disabled={disabled}
                multiple={isMultiple}
                accept={extensionsToAccept}
                onChange={onSubmitBrowsedFile}
                className={inputFilePickerClassName}
            />
        </Stack>
    );

    return (
        <Stack
            onDrop={handleDrop}
            onDragOver={onDragOver}
            tokens={{ padding: 10 }}
            data-automation-id="droppingFilesArea"
            onDragLeave={() => setIsDragging(false)}
            styles={mergeStyleSets(containerStyles, draggingStyles)}>
            {onRenderContent ? onRenderContent(defaultRenderer) : defaultRenderer}
        </Stack>
    );
};
